import styled, { css, type Interpolation, keyframes } from 'styled-components';

import { ellipsisCss } from 'components/core/typography/Text';

import { DISABLED_INPUT_OPACITY } from 'styles/forms';
import {
  BLUE_050,
  BLUE_500,
  BLUE_600,
  BLUE_700,
  BORDER_RADIUS_100,
  BORDER_RADIUS_200,
  BOX_SHADOW_100,
  BOX_SHADOW_300,
  FONT_FAMILY,
  FONT_SIZE_14,
  FONT_SIZE_16,
  FONT_WEIGHT_MEDIUM,
  GREEN_500,
  GREEN_600,
  GREEN_700,
  HEIGHT_400,
  HEIGHT_500,
  HEIGHT_650,
  HEIGHT_700,
  HEIGHT_750,
  HEIGHT_850,
  LETTER_SPACING_DENSE,
  LINE_HEIGHT_2,
  NEUTRAL_0,
  NEUTRAL_050,
  NEUTRAL_100,
  NEUTRAL_200,
  NEUTRAL_700,
  NEUTRAL_800,
  NEUTRAL_900,
  RED_500,
  RED_600,
  RED_700,
  SPACE_050,
  SPACE_100,
  SPACE_150,
  SPACE_200,
  SPACE_300,
  SPACE_400,
  WIDTH_400,
  WIDTH_500,
  Z_INDEX_1,
} from 'styles/tokens';

export type ButtonSize = 'large' | 'medium' | 'small' | 'x-small';

export type ButtonVariant = 'destructive' | 'ghost' | 'neutral' | 'positive' | 'primary' | 'secondary';

/**
 * Internal-only props for StyledButton — not part of Button's public Props.
 * Used by IconButton when rendering StyledButton directly.
 */
export interface StyledButtonInternalProps {
  $buttonStyleOverride?: Interpolation<object>;
  $iconOnly?: boolean;
}

export interface StyledButtonProps {
  $active?: boolean;
  $fullWidth?: boolean;
  $loading?: boolean;
  $size?: ButtonSize;
  $variant?: ButtonVariant;
}

const FOCUS_OUTLINE_COLOR = BLUE_500;
const TRANSITION_DURATION = '100ms';

const primaryStyles = css<StyledButtonProps>`
  background-color: ${BLUE_500};
  box-shadow: ${BOX_SHADOW_300};
  color: ${NEUTRAL_0};

  &:hover:not(:disabled) {
    background-color: ${BLUE_600};
  }

  &:active:not(:disabled) {
    background-color: ${BLUE_700};
    box-shadow: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${BLUE_700};
      box-shadow: none;
    `}
`;

const secondaryStyles = css<StyledButtonProps>`
  background-color: ${NEUTRAL_0};
  box-shadow:
    inset 0 0 0 1px ${NEUTRAL_100},
    ${BOX_SHADOW_100};
  color: ${NEUTRAL_900};

  &:hover:not(:disabled) {
    background-color: ${NEUTRAL_050};
    box-shadow:
      inset 0 0 0 1px ${NEUTRAL_200},
      ${BOX_SHADOW_100};
  }

  &:active:not(:disabled) {
    background-color: ${NEUTRAL_100};
    box-shadow: inset 0 0 0 1px ${NEUTRAL_200};
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${NEUTRAL_100};
      box-shadow: inset 0 0 0 1px ${NEUTRAL_200};
    `}
`;

const neutralStyles = css<StyledButtonProps>`
  background-color: ${NEUTRAL_900};
  box-shadow: ${BOX_SHADOW_300};
  color: ${NEUTRAL_0};

  &:hover:not(:disabled) {
    background-color: ${NEUTRAL_800};
  }

  &:active:not(:disabled) {
    background-color: ${NEUTRAL_700};
    box-shadow: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${NEUTRAL_700};
      box-shadow: none;
    `}
`;

const ghostStyles = css<StyledButtonProps>`
  background-color: transparent;
  color: ${NEUTRAL_900};

  &:hover:not(:disabled) {
    background-color: ${NEUTRAL_050};
  }

  &:active:not(:disabled) {
    background-color: ${NEUTRAL_100};
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${NEUTRAL_100};
    `}
`;

const positiveStyles = css<StyledButtonProps>`
  background-color: ${GREEN_500};
  box-shadow: ${BOX_SHADOW_300};
  color: ${NEUTRAL_0};

  &:hover:not(:disabled) {
    background-color: ${GREEN_600};
  }

  &:active:not(:disabled) {
    background-color: ${GREEN_700};
    box-shadow: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${GREEN_700};
      box-shadow: none;
    `}
`;

const destructiveStyles = css<StyledButtonProps>`
  background-color: ${RED_500};
  box-shadow: ${BOX_SHADOW_300};
  color: ${NEUTRAL_0};

  &:hover:not(:disabled) {
    background-color: ${RED_600};
  }

  &:active:not(:disabled) {
    background-color: ${RED_700};
    box-shadow: none;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: ${RED_700};
      box-shadow: none;
    `}
`;

const xsSizeStyles = css`
  font-size: ${FONT_SIZE_14};
  min-height: ${HEIGHT_650};
  padding: ${SPACE_100} ${SPACE_200};

  & svg {
    height: ${HEIGHT_400};
    width: ${WIDTH_400};
  }
`;

const smallSizeStyles = css`
  font-size: ${FONT_SIZE_14};
  min-height: ${HEIGHT_700};
  padding: ${SPACE_150} ${SPACE_300};

  & svg {
    height: ${HEIGHT_400};
    width: ${WIDTH_400};
  }
`;

const mediumSizeStyles = css`
  font-size: ${FONT_SIZE_14};
  min-height: ${HEIGHT_750};
  padding: ${SPACE_200} ${SPACE_400};

  & svg {
    height: ${HEIGHT_500};
    width: ${WIDTH_500};
  }
`;

const largeSizeStyles = css`
  font-size: ${FONT_SIZE_16};
  min-height: ${HEIGHT_850};
  padding: ${SPACE_300} ${SPACE_400};

  & svg {
    height: ${HEIGHT_500};
    width: ${WIDTH_500};
  }
`;

const sizeStyles = {
  large: largeSizeStyles,
  medium: mediumSizeStyles,
  small: smallSizeStyles,
  'x-small': xsSizeStyles,
} satisfies Record<ButtonSize, ReturnType<typeof css>>;

const iconOnlySizeStyles: Record<ButtonSize, ReturnType<typeof css>> = {
  large: css`
    padding: ${SPACE_300};
  `,
  medium: css`
    padding: ${SPACE_200};
  `,
  small: css`
    padding: ${SPACE_200};
  `,
  'x-small': css`
    padding: ${SPACE_150};
  `,
};

const variantStyles = {
  destructive: destructiveStyles,
  ghost: ghostStyles,
  neutral: neutralStyles,
  positive: positiveStyles,
  primary: primaryStyles,
  secondary: secondaryStyles,
} satisfies Record<ButtonVariant, ReturnType<typeof css>>;

const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

export const SpinnerContainer = styled.span`
  align-items: center;
  animation: ${rotate} 1s linear infinite;
  display: inline-flex;
  justify-content: center;
`;

export const StyledButton = styled.button<StyledButtonInternalProps & StyledButtonProps>`
  align-items: center;
  border: none;
  border-radius: ${BORDER_RADIUS_200};
  column-gap: ${SPACE_200};
  cursor: pointer;
  display: inline-flex;
  font-family: ${FONT_FAMILY};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  justify-content: center;
  letter-spacing: ${LETTER_SPACING_DENSE};
  line-height: ${LINE_HEIGHT_2};
  max-width: 100%;
  position: relative;
  transition: all ${TRANSITION_DURATION} ease-in-out;

  /* Variant styles */
  ${({ $variant = 'secondary' }) => variantStyles[$variant]}

  /* Size styles */
  ${({ $size = 'medium' }) => sizeStyles[$size]}

  /* Full width */
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
    opacity: ${DISABLED_INPUT_OPACITY};
  }

  /* Loading state */
  ${({ $loading }) =>
    $loading &&
    css`
      pointer-events: none;
    `}

  /* Active press effect */
  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  /* Focus visible state */
  &:focus-visible {
    outline: ${SPACE_050} solid ${FOCUS_OUTLINE_COLOR};
    outline-offset: ${SPACE_050};
  }

  /* Icon-only mode: override padding to be square */
  ${({ $iconOnly, $size = 'medium' }) => $iconOnly && iconOnlySizeStyles[$size]}

  /* External style override (deprecated escape hatch, used by IconButton internally) */
  ${({ $buttonStyleOverride }) => $buttonStyleOverride}
`;

export const ButtonContent = styled.span<{
  $loading?: boolean;
  $styleOverride?: Interpolation<object>;
}>`
  align-items: center;
  display: inline-flex;
  gap: ${SPACE_200};
  max-width: 100%;
  opacity: ${({ $loading }) => ($loading ? 0 : 1)};

  ${({ $styleOverride }) => $styleOverride}
`;

export const IconWrapper = styled.span`
  align-items: center;
  display: inline-flex;
`;

export const LoadingOverlay = styled.span`
  align-items: center;
  display: flex;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: ${Z_INDEX_1};
`;

export const ButtonText = styled.span`
  ${ellipsisCss}
`;

export const CountBadge = styled.span`
  align-items: center;
  background-color: ${BLUE_050};
  border-radius: ${BORDER_RADIUS_100};
  color: ${BLUE_700};
  display: inline-flex;
  font-size: calc(1em - 2px);
  font-weight: ${FONT_WEIGHT_MEDIUM};
  justify-content: center;
  line-height: inherit;
  min-width: ${WIDTH_500};
  padding: 0 ${SPACE_100};
`;
